import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { BaseInputComponent } from '../base-input/base-input.component';
import { AutocompleteInputType } from '../form.types';
import { QuestionStateService } from '../question-state.service';

@Component({
    selector: 'app-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class AutocompleteComponent extends BaseInputComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() input: AutocompleteInputType;
    @ViewChild(MatInput) inputElement: MatInput;
    options: Array<any>;
    private localOptions: Array<any>;
    loading: boolean;
    errorLoading: boolean;
    searchControl: UntypedFormControl = new UntypedFormControl('');
    private subscription$: Subscription;
    private pipeSubscription$: Subscription;
    noSearchResultsFound: boolean;
    constructor(
        private http: HttpClient,
        private questionStateService: QuestionStateService,
        private changeDetection: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.initialize(this.input, this.questionStateService);
        this.subscribe();
        this.getInitialValues();
        if (this.input.control.disabled) {
            this.searchControl.disable();
        }
        if (this.input.useLocalOptions) {
            this.localOptions = this.input.localOptions;
            this.options = this.input.localOptions;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.input.updateLocalOptions) {
            this.localOptions = this.input.localOptions;
            this.options = [];
            this.input.updateLocalOptions = false;
        }
    }

    ngAfterViewInit() {
        if (this.inputElement) {
            this.inputIdEvent.emit(this.inputElement.id);
        }
        this.changeDetection.detectChanges();
        this.input.control.registerOnDisabledChange((isDisabled) => {
            if (isDisabled) {
                this.searchControl.disable();
            } else {
                this.searchControl.enable();
            }
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription$) {
            this.subscription$.unsubscribe();
        }
        if (this.pipeSubscription$) {
            this.pipeSubscription$.unsubscribe();
        }
    }

    getInitialValues() {
        if (this.input.baseUrl && this.input.control.value && !this.input.useLocalOptions) {
            const value = this.input.control.value;
            this.loading = true;
            this.http
                .get<Array<any>>(`/api/${this.input.baseUrl}?${this.input.queryParam}=${value}&initialValue=true`)
                .subscribe({
                    next: (data) => {
                        this.loading = false;
                        if (data && data.length > 1) {
                            data = [data.find((m) => m[this.input.property] === value)];
                        }
                        if (data && data.length === 1) {
                            this.options = data;
                            this.searchControl.patchValue(data[0]);
                        } else {
                            this.errorLoading = true;
                        }
                    },
                    error: () => {
                        this.loading = false;
                        this.errorLoading = true;
                    }
                });
        }
    }

    subscribe() {
        this.input.queryParam = this.input.queryParam ? this.input.queryParam : 'q';
        if (this.input.baseUrl || this.input.useLocalOptions) {
            this.pipeSubscription$ = this.searchControl.valueChanges
                .pipe(
                    debounceTime(300),
                    map((value) => {
                        if ((value && value.length > 2) || this.input.useLocalOptions) {
                            return value;
                        }
                    })
                )
                .subscribe((value) => {
                    if (!value) {
                        if (this.input.useLocalOptions) {
                            this.options = this.localOptions;
                        }
                        return;
                    }
                    this.loading = true;
                    if (this.input.useLocalOptions) {
                        this.loading = false;
                        this.options = this.localOptions.filter((m) =>
                            this.renderDisplay(m).toLowerCase().includes(value.toString().toLowerCase())
                        );
                    } else {
                        if (this.subscription$) {
                            this.subscription$.unsubscribe();
                        }
                        this.subscription$ = this.http
                            .get<Array<any>>(`/api/${this.input.baseUrl}?${this.input.queryParam}=${value}`)
                            .subscribe({
                                next: (data) => {
                                    this.loading = false;
                                    this.options = data;
                                    this.noSearchResultsFound = false;
                                },
                                error: (error) => {
                                    this.loading = false;
                                    if (error.status !== 404) {
                                        this.errorLoading = true;
                                    } else {
                                        this.noSearchResultsFound = true;
                                    }
                                }
                            });
                    }
                });
        }
    }

    selected($event: MatAutocompleteSelectedEvent) {
        this.input.control.setValue($event.option.value[this.input.property]);
        this.input.control.markAsDirty();
        this.loading = false;
    }

    setValue(): boolean {
        const v = this.searchControl.value;
        if (this.input.allowAny && typeof v === 'string') {
            this.input.control.setValue(v);
            this.input.control.markAsDirty();
            return true;
        }
        return false;
    }

    autoBlurEvent($event: FocusEvent) {
        if (this.input.allowAny) {
            this.setValue();
        }
        if (!this.searchControl.value) {
            this.input.control.setValue('');
            this.input.control.markAsDirty();
        }
        if (this.input.useLocalOptions && this.reflection.isNullOrEmpty(this.input.control.value)) {
            this.options = this.input.localOptions;
        }
        this.blurEvent($event);
        this.loading = false;
    }

    renderDisplay(option: any): string {
        if (!option) {
            return '';
        }
        let toReturn: string = option[this.input.display];
        if (this.input.displayCallBack) {
            toReturn = this.input.displayCallBack.function.call(this.input.displayCallBack.this, option);
        }
        return toReturn;
    }

    autoFocusEvent($event: FocusEvent) {
        if (this.input.useLocalOptions && this.reflection.isNullOrEmpty(this.input.control.value)) {
            this.options = this.input.localOptions;
        }
        super.focusEvent($event);
    }
}
